import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { App, Form, Skeleton, Spin } from "antd";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FormServiceLayout, FormTcfVendor, useFormTcfVendorHandler, useFormTcfVendorProvider } from "@devowl-wp/react-cookie-banner-admin";
import { scrollTo } from "@devowl-wp/react-utils";
import { useRouteTcfVendorConfiguration } from "../../../../hooks/useRouteTcfVendorConfiguration.js";
import { TcfVendorConfigurationModel } from "../../../../models/tcfVendorConfigurationModel.js";
import { useStores } from "../../../../store/stores.js";
import { ConfigContent } from "../../content.js";
const TcfVendorConfigurationForm = observer(({ vendor, navigateAfterCreation = true, scrollToTop = true, onCreated })=>{
    const { message } = App.useApp();
    const { vendorConfiguration, id, queried, fetched, link } = useRouteTcfVendorConfiguration();
    const navigate = useNavigate();
    const { tcfStore, optionStore: { territorialLegalBasis, others: { iso3166OneAlpha2, frontend: { predefinedDataProcessingInSafeCountriesLists } } } } = useStores();
    const { vendorConfigurations, declarations } = tcfStore;
    const [useVendor, setUseVendor] = useState(vendor);
    const { prompt, form, isBusy, defaultValues, overrideValues, onFinish, onFinishFailed, onValuesChange, contextValue } = useFormTcfVendorHandler({
        isEdit: fetched,
        handleSave: async (values)=>{
            const { status, restrictivePurposes = {
                normal: {}
            }, dataProcessingInCountries, dataProcessingInCountriesSpecialTreatments, ...meta } = values;
            try {
                const newMeta = {
                    ...meta,
                    vendorId: useVendor.data.id,
                    restrictivePurposes: JSON.stringify(restrictivePurposes),
                    dataProcessingInCountries: JSON.stringify(dataProcessingInCountries),
                    dataProcessingInCountriesSpecialTreatments: JSON.stringify(dataProcessingInCountriesSpecialTreatments)
                };
                delete newMeta.templateCheck;
                if (queried) {
                    vendorConfiguration.setStatus(status);
                    vendorConfiguration.setMeta(newMeta);
                    await vendorConfiguration.patch();
                } else {
                    const draft = new TcfVendorConfigurationModel(vendorConfigurations, {
                        status,
                        meta: {
                            ...newMeta
                        }
                    });
                    await draft.persist();
                    onCreated?.(draft);
                }
                return ()=>navigateAfterCreation && navigate(link.slice(1));
            } catch (e) {
                message.error(e.responseJSON.message);
                throw e;
            }
        },
        declarations,
        vendor: useVendor?.data
    });
    const initialValues = overrideValues(fetched ? {
        status: vendorConfiguration.data.status,
        restrictivePurposes: JSON.parse(JSON.stringify(vendorConfiguration.restrictivePurposes)),
        dataProcessingInCountries: JSON.parse(JSON.stringify(vendorConfiguration.dataProcessingInCountries)),
        dataProcessingInCountriesSpecialTreatments: JSON.parse(JSON.stringify(vendorConfiguration.dataProcessingInCountriesSpecialTreatments)),
        templateCheck: true
    } : defaultValues);
    useEffect(()=>{
        if (vendorConfiguration.vendorModel) {
            setUseVendor(vendorConfiguration.vendorModel);
        }
    }, [
        vendorConfiguration
    ]);
    // Initially load the vendor configuration if not yet done
    useEffect(()=>{
        if (queried && !fetched) {
            vendorConfigurations.getSingle({
                params: {
                    id,
                    context: "edit"
                }
            });
        }
    }, [
        queried,
        fetched
    ]);
    // Scroll to top when opening the form
    useEffect(()=>{
        if (scrollToTop) {
            scrollTo(0);
        }
    }, []);
    const notReady = queried && !fetched || !useVendor || !declarations;
    const [FormTcfVendorContextProvider, formTcfVendorContextValue] = useFormTcfVendorProvider({
        ...contextValue,
        territorialLegalBasis,
        iso3166OneAlpha2,
        predefinedDataProcessingInSafeCountriesLists
    }, {}, {
        inherit: [
            "vendor"
        ],
        deps: [
            notReady
        ]
    });
    if (notReady) {
        return /*#__PURE__*/ _jsx(ConfigContent, {
            maxWidth: "fixed",
            children: /*#__PURE__*/ _jsx(Skeleton, {
                active: true,
                paragraph: {
                    rows: 8
                }
            })
        });
    }
    return /*#__PURE__*/ _jsx(ConfigContent, {
        maxWidth: "fixed",
        children: /*#__PURE__*/ _jsx(FormTcfVendorContextProvider, {
            value: formTcfVendorContextValue,
            children: /*#__PURE__*/ _jsxs(Spin, {
                spinning: isBusy,
                children: [
                    prompt,
                    /*#__PURE__*/ _jsx(Form, {
                        name: `tcf-vendor-${id}`,
                        form: form,
                        ...FormServiceLayout,
                        initialValues: initialValues,
                        onFinish: onFinish,
                        onFinishFailed: onFinishFailed,
                        onValuesChange: onValuesChange,
                        scrollToFirstError: {
                            behavior: "smooth",
                            block: "center"
                        },
                        labelWrap: true,
                        children: /*#__PURE__*/ _jsx(FormTcfVendor, {})
                    })
                ]
            })
        })
    });
});
export { TcfVendorConfigurationForm };
